import { AppConfig } from '../../../../AppConfig'
import { AppUiHelper } from '../../../../helpers/AppUiHelper'
import { WhatsappUtils } from '../WhatsappUtils'
import { emptyWaBodyRichText, whatsAppBodyTextToWaBodyRichText } from '../WhatsappTemplateDetail'
import { normalizeNodeData } from './chatbotGraph'

function extractFilename(path) {
  if (typeof path !== 'string' || !path) return ''
  const parts = path.split('/')
  const last = parts[parts.length - 1] || ''
  try {
    return decodeURIComponent(last)
  } catch (e) {
    return last
  }
}

function readIndexedButtons(formData) {
  const buttons = []
  for (let i = 1; i <= 3; i++) {
    const text = String(formData['button' + i + 'Text'] || '').trim()
    if (!text) continue
    const type = String(formData['button' + i + 'Type'] || '').trim().toUpperCase() || 'QUICK_REPLY'
    const button = { id: str(formData['button' + i + 'Id']), type, text }
    if (type === 'URL') {
      button.url = String(formData['button' + i + 'Url'] || '').trim()
    }
    if (type === 'FLOW') {
      button.flowId = String(formData['button' + i + 'FlowId'] || '').trim()
    }
    buttons.push(button)
  }
  return buttons
}

function readIndexedListRows(formData) {
  const rows = []
  for (let i = 1; i <= 10; i++) {
    const title = String(formData['row' + i + 'Title'] || '').trim()
    if (!title) continue
    rows.push({
      id: str(formData['row' + i + 'Id']),
      title,
      description: String(formData['row' + i + 'Description'] || '').trim(),
    })
  }
  return rows
}

function bodyRichTextFromNode(d) {
  if (d && d.bodyRichText) return d.bodyRichText
  if (d && d.bodyText) return whatsAppBodyTextToWaBodyRichText(d.bodyText)
  return emptyWaBodyRichText()
}

function str(value) {
  return String(value || '').trim()
}

function takenIds(prevItems, items) {
  const taken = {}
  prevItems.concat(items).forEach(item => {
    if (item && item.id) taken[item.id] = true
  })
  return taken
}

function nextFreeId(prefix, taken) {
  let n = 1
  while (taken[prefix + n]) n++
  taken[prefix + n] = true
  return prefix + n
}

function fieldLinkFromCaller(caller, linkName) {
  const fields = caller && caller.fields
  if (!Array.isArray(fields) || !linkName) return ''
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]
    if (f && f.linkName === linkName && f.link) return f.link
  }
  return ''
}

export function uploadLinksFromForm(formData, caller) {
  const fd = formData || {}
  const state = caller && caller.state
  const pick = (linkName) => (
    (state && state[linkName]) || fd[linkName] || fieldLinkFromCaller(caller, linkName) || ''
  )
  return {
    imageLink: pick('imageLink'),
    videoLink: pick('videoLink'),
    attachmentsLink: pick('attachmentsLink'),
  }
}

function keepMedia(fd, prevData, fileKey, linkKey) {
  const media = str(fd[fileKey])
  const fromForm = str(fd[linkKey])
  if (fromForm) return { media, mediaLink: fromForm }
  if (!media) return { media: '', mediaLink: '' }
  const prev = normalizeNodeData(prevData)
  if (media === str(prev.media)) return { media, mediaLink: str(prev.mediaLink) }
  return { media, mediaLink: '' }
}

export function getChatbotMessageFieldDef(flowOptions, crmContactsFieldDef) {
  const flowOpts = Array.isArray(flowOptions) ? flowOptions : []
  return {
    title: 'Send Message',
    formType: 'edit',
    ctaButtonText: 'Save',
    preview: {
      component: 'chatbot_session_preview_adapter',
      title: 'Preview',
    },
    steps: [{
      fields: [
        {
          key: 'headerType',
          type: 'radio',
          label: 'Message header',
          options: [
            { key: 'NONE', label: 'NONE' },
            { key: 'TEXT', label: 'TEXT' },
            { key: 'IMAGE', label: 'IMAGE' },
            { key: 'VIDEO', label: 'VIDEO' },
            { key: 'DOCUMENT', label: 'DOCUMENT' },
          ],
          defaultValue: 'NONE',
          v_required: { value: true, err: 'Provide header type' },
          placement: 'left',
        },
        {
          key: 'footerText',
          type: 'edit_text',
          label: 'Footer Text',
          tip: 'Optional footer',
          v_required: { value: false, err: '' },
          v_max: { value: 60, err: 'Footer text should not be more than 60 characters' },
          placement: 'right',
        },
        {
          key: 'headerText',
          type: 'edit_text',
          label: 'Header text',
          v_required: { value: true, err: 'Provide header text' },
          v_max: { value: 60, err: 'Header text should not be more than 60 characters' },
          placement: 'left',
          condition: { key: 'headerType', value: 'TEXT', values: ['TEXT'] },
        },
        {
          key: 'image',
          linkName: 'imageLink',
          type: 'edit_text',
          label: 'Image (PNG/JPEG, max 1920px height)',
          imageHeight: 1920,
          mode: 'fit',
          tip: 'Upload an image in PNG or JPEG format',
          v_required: { value: true, err: 'Provide image' },
          v_min: { value: 1, err: 'Invalid image' },
          v_max: { value: 100, err: 'Invalid image' },
          imageUpload: true,
          uploadType: 'media',
          uploadAccept: 'image/png, image/jpeg, image/jpg',
          placement: 'left',
          condition: { key: 'headerType', value: 'IMAGE', values: ['IMAGE'] },
          uploadMs: AppConfig.ENDPOINT,
        },
        {
          key: 'video',
          linkName: 'videoLink',
          label: 'Video (MP4, max 16MB file size)',
          hint: 'Upload a video in MP4 format',
          v_required: { value: true, err: 'Provide video' },
          placement: 'left',
          type: 'edit_text',
          subType: 'VIDEO',
          documentUpload: true,
          v_min: { value: 1, err: 'Invalid video' },
          v_max: { value: 100, err: 'Invalid video' },
          uploadType: 'media',
          uploadAccept: 'video/mp4',
          fileSizeLimit: 16000000,
          condition: { key: 'headerType', value: 'VIDEO', values: ['VIDEO'] },
          uploadMs: AppConfig.ENDPOINT,
        },
        {
          key: 'attachment',
          linkName: 'attachmentsLink',
          label: 'Document',
          hint: 'Upload PDF document (max 20MB)',
          v_required: { value: true, err: 'Provide document' },
          placement: 'left',
          type: 'edit_text',
          documentUpload: true,
          v_min: { value: 1, err: 'Invalid document' },
          v_max: { value: 100, err: 'Invalid document' },
          uploadType: 'media',
          uploadAccept: 'application/pdf',
          fileSizeLimit: 20000000,
          condition: { key: 'headerType', value: 'DOCUMENT', values: ['DOCUMENT'] },
          uploadMs: AppConfig.ENDPOINT,
        },
        {
          key: 'fileName',
          type: 'edit_text',
          label: 'File Name',
          tip: 'Set the document filename to send',
          v_required: { value: true, err: 'Provide file name' },
          placement: 'right',
          condition: { key: 'headerType', value: 'DOCUMENT', values: ['DOCUMENT'] },
        },
        {
          key: 'bodyRichText',
          type: 'rich_text',
          toolbarFlags: 'CLEAR_FORMATTING, FORMAT_BOLD, FORMAT_ITALIC, BULLETS',
          label: 'Message Body / Caption',
          tip: 'Bold, italic, and bullets. Insert Custom Fields for contact values.',
          variablePicker: {
            title: 'Custom Fields',
            options: AppUiHelper.getContactTemplateVariables(crmContactsFieldDef),
          },
          v_required: { value: true, err: 'Provide message body' },
          placement: 'left',
        },
        {
          key: 'interactivityType',
          type: 'radio',
          label: 'Interactivity Type',
          options: [
            { key: 'NONE', label: 'NONE' },
            { key: 'BUTTON', label: 'BUTTON' },
            { key: 'LIST', label: 'LIST' },
          ],
          v_required: { value: true, err: 'Choose interactivity type' },
          placement: 'left',
          defaultValue: 'NONE',
        },
        {
          key: 'interactivityLabel',
          type: 'edit_text',
          condition: { key: 'interactivityType', value: 'LIST', values: ['LIST'] },
          label: 'Label',
          v_required: { value: true, err: 'Label is required' },
          v_max: { value: 20, err: 'Label should not be more than 20 characters' },
          placement: 'left',
        },
        {
          key: 'sectionTitle',
          type: 'edit_text',
          condition: { key: 'interactivityType', value: 'LIST', values: ['LIST'] },
          label: 'List Section Title',
          v_required: { value: true, err: 'List section title is required' },
          v_max: { value: 24, err: 'List section title should not be more than 24 characters' },
          placement: 'left',
        },
        {
          key: 'button',
          type: 'list',
          condition: { key: 'interactivityType', value: 'BUTTON', values: ['BUTTON'] },
          v_required: { value: true, err: 'Add at least one button' },
          listConfig: {
            title: 'Buttons',
            addButtonText: 'Add Button',
            maxItems: 3,
            minItems: 1,
            reorderable: true,
            addRowConstraint: {
              rowFieldKey: 'Type',
              maxItemsByValue: { URL: 1, FLOW: 1 },
              lockToFirstRowValue: true,
            },
            rowFields: [
              {
                key: 'Type',
                type: 'radio',
                label: 'Type',
                options: [
                  { key: 'QUICK_REPLY', label: 'QUICK REPLY' },
                  { key: 'URL', label: 'URL' },
                  { key: 'FLOW', label: 'FLOW' },
                ],
                v_required: { value: true, err: 'Type is required' },
              },
              {
                key: 'Text',
                type: 'edit_text',
                label: 'Label',
                v_required: { value: true, err: 'Label is required' },
                v_max: { value: 20, err: 'Button label should not be more than 20 characters' },
              },
              {
                key: 'Url',
                type: 'edit_text',
                condition: { key: 'Type', value: 'URL' },
                label: 'URL',
                v_required: { value: true, err: 'URL is required' },
              },
              {
                key: 'FlowId',
                type: 'radio',
                condition: { key: 'Type', value: 'FLOW' },
                label: 'Flow',
                options: flowOpts,
                v_required: { value: true, err: 'Flow is required' },
              },
              { key: 'Id', type: 'hidden' },
            ],
          },
        },
        {
          key: 'row',
          type: 'list',
          condition: { key: 'interactivityType', value: 'LIST', values: ['LIST'] },
          v_required: { value: true, err: 'Add at least one row' },
          listConfig: {
            title: 'Rows',
            addButtonText: 'Add Row',
            maxItems: 10,
            minItems: 1,
            reorderable: true,
            flatStopAtEmptyRowKey: 'Title',
            rowFields: [
              {
                key: 'Title',
                type: 'edit_text',
                label: 'Title',
                v_required: { value: true, err: 'Title is required' },
                v_max: { value: 24, err: 'Row title should not be more than 24 characters' },
              },
              {
                key: 'Description',
                type: 'edit_text',
                label: 'Description',
                v_required: { value: false, err: 'Description is required' },
                v_max: { value: 72, err: 'Row description should not be more than 72 characters' },
              },
              { key: 'Id', type: 'hidden' },
            ],
          },
        },
      ],
    }],
  }
}

export function nodeDataToFormItem(data) {
  const d = normalizeNodeData(data)
  const item = {
    headerType: d.headerType,
    headerText: d.headerText,
    bodyRichText: bodyRichTextFromNode(d),
    footerText: d.footerText,
    interactivityType: d.interactivityType,
    interactivityLabel: d.listLabel,
    sectionTitle: d.listSectionTitle,
    image: '',
    imageLink: '',
    video: '',
    videoLink: '',
    attachment: '',
    attachmentsLink: '',
    fileName: d.fileName || '',
  }
  if (d.headerType === 'IMAGE') {
    item.image = d.media
    item.imageLink = d.mediaLink
  } else if (d.headerType === 'VIDEO') {
    item.video = d.media
    item.videoLink = d.mediaLink
  } else if (d.headerType === 'DOCUMENT') {
    item.attachment = d.media
    item.attachmentsLink = d.mediaLink
  }
  const buttons = d.buttons || []
  for (let i = 0; i < buttons.length; i++) {
    const n = i + 1
    item['button' + n + 'Id'] = buttons[i].id || ''
    item['button' + n + 'Type'] = buttons[i].type || 'QUICK_REPLY'
    item['button' + n + 'Text'] = buttons[i].text || ''
    item['button' + n + 'Url'] = buttons[i].url || ''
    item['button' + n + 'FlowId'] = buttons[i].flowId || ''
  }
  const rows = d.listRows || []
  for (let i = 0; i < rows.length; i++) {
    const n = i + 1
    item['row' + n + 'Id'] = rows[i].id || ''
    item['row' + n + 'Title'] = rows[i].title || ''
    item['row' + n + 'Description'] = rows[i].description || ''
  }
  return item
}

export function formDataToNodeData(formData, prevData) {
  const fd = formData || {}
  const headerType = fd.headerType || 'NONE'
  const interactivityType = fd.interactivityType || 'NONE'
  const bodyRichText = fd.bodyRichText || emptyWaBodyRichText()
  const out = {
    headerType,
    bodyRichText,
    bodyText: WhatsappUtils.waRichTextToWhatsAppBodyText(bodyRichText),
    interactivityType,
  }
  if (headerType === 'TEXT') {
    out.headerText = fd.headerText || ''
  }
  if (headerType === 'IMAGE') {
    Object.assign(out, keepMedia(fd, prevData, 'image', 'imageLink'))
  } else if (headerType === 'VIDEO') {
    Object.assign(out, keepMedia(fd, prevData, 'video', 'videoLink'))
  } else if (headerType === 'DOCUMENT') {
    Object.assign(out, keepMedia(fd, prevData, 'attachment', 'attachmentsLink'))
    out.fileName = fd.fileName || extractFilename(out.media) || ''
  }
  const footer = String(fd.footerText || '').trim()
  if (footer) out.footerText = footer

  if (interactivityType === 'BUTTON') {
    const buttons = readIndexedButtons(fd)
    const taken = takenIds(normalizeNodeData(prevData).buttons, buttons)
    out.buttons = buttons.map(b => {
      const next = {
        id: b.id || nextFreeId('btn', taken),
        type: b.type || 'QUICK_REPLY',
        text: b.text,
      }
      if (next.type === 'URL' && b.url) next.url = b.url
      if (next.type === 'FLOW' && b.flowId) next.flowId = b.flowId
      return next
    })
  } else if (interactivityType === 'LIST') {
    out.listLabel = String(fd.interactivityLabel || '').trim()
    out.listSectionTitle = String(fd.sectionTitle || '').trim()
    const rows = readIndexedListRows(fd)
    const taken = takenIds(normalizeNodeData(prevData).listRows, rows)
    out.listRows = rows.map(r => ({
      id: r.id || nextFreeId('row', taken),
      title: r.title,
      description: r.description,
    }))
  }
  if (prevData && prevData.defaultEnabled) out.defaultEnabled = true
  return out
}

export function patchMessageContactVariables(fieldDef, crmContactsFieldDef) {
  const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
  if (!Array.isArray(fields)) return false
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i]
    if (field && field.key === 'bodyRichText' && field.variablePicker) {
      field.variablePicker.options = AppUiHelper.getContactTemplateVariables(crmContactsFieldDef)
      return true
    }
  }
  return false
}

export function patchMessageFlowOptions(fieldDef, options) {
  const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
  if (!Array.isArray(fields)) return false
  for (let i = 0; i < fields.length; i++) {
    const list = fields[i]
    if (!list || list.key !== 'button' || !list.listConfig || !Array.isArray(list.listConfig.rowFields)) continue
    const rows = list.listConfig.rowFields
    for (let j = 0; j < rows.length; j++) {
      if (rows[j] && rows[j].key === 'FlowId') {
        rows[j].options = options
        return true
      }
    }
  }
  return false
}
